import { Store } from '@ngrx/store';
import { ErrorTracking } from '@shared/tracking';
import { Observable } from 'rxjs/Observable';
import { combineLatest, distinctUntilChanged, filter, map, pairwise, shareReplay, switchMap, take, tap, } from 'rxjs/operators';
import { ApiHttp } from './api-http.service';
import { arrayIsShallowEqual, isDefined, objectFilterByKeys, sameElements, setDiff, toNumber, toObservable, } from './helpers';
import { push } from './http-actions';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { Query } from './query';
import { SearchManager } from './search';
import { stringifyQuery } from './store.helpers';
import { WebSocketService } from './websocket';
var Datastore = /** @class */ (function () {
    function Datastore(store, apiHttp, webSocketService, errorTracking, searchManager) {
        this.store = store;
        this.apiHttp = apiHttp;
        this.webSocketService = webSocketService;
        this.errorTracking = errorTracking;
        this.searchManager = searchManager;
    }
    Datastore.prototype.list = function (key1, key2, queryFn) {
        var _this = this;
        var rawQuery = queryFn ? queryFn(new Query()).query : {};
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(rawQuery.limit),
            toObservable(rawQuery.offset),
            toObservable(rawQuery.params),
        ], function (collection, authUid, limit, offset, params) {
            var path = { collection: collection, authUid: authUid };
            return { path: path, query: { limit: limit, offset: offset, params: params } };
        }).pipe(tap(function (ref) {
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: ref.path.collection,
                    ref: ref,
                },
            };
            _this.store.dispatch(action);
        }), shareReplay(1));
        return new ListObservable(refStream, this.apiHttp, this.store, this.errorTracking, refStream.pipe(switchMap(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid, query = _a.query;
            return _this.store.pipe(tap(function (source) {
                if (!(collection in source)) {
                    throw new Error("Missing collection \"" + collection + "\", please import the corresponding feature module");
                }
            }), select(collection, authUid), map(function (data) {
                var queryString = stringifyQuery(query);
                if (data && data.lists && data.lists[queryString]) {
                    return ((data.lists[queryString] || { ids: [] }).ids || [])
                        .slice((query && query.offset) || 0)
                        .map(function (id) { return data.entities[id].entity; });
                }
                return undefined;
            }), filter(isDefined), distinctUntilChanged(arrayIsShallowEqual));
        }), shareReplay(1)));
    };
    /* Returns an observable with a single object.
     */
    Datastore.prototype.object = function (key1, key2, objectId) {
        var _this = this;
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(objectId).pipe(distinctUntilChanged()),
        ], function (collection, authUid, id) {
            var path = {
                collection: collection,
                authUid: authUid,
                ids: [id.toString()],
            };
            return { path: path };
        }).pipe(switchMap(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid;
            return _this.store.pipe(tap(function (source) {
                if (!(collection in source)) {
                    throw new Error("Missing collection \"" + collection + "\", please import the corresponding feature module");
                }
            }), select(collection, authUid), take(1));
        }, function (_a, data) {
            var path = _a.path;
            return ({ ref: { path: path }, data: data });
        }), tap(function (_a) {
            var ref = _a.ref, data = _a.data;
            var ids = ref.path.ids;
            // Don't fetch if this was fetched in the last second.
            if (!data ||
                !data.entities[ids[0]] ||
                toNumber(data.entities[ids[0]].timeFetched) + 10000 < Date.now()) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: {
                        type: ref.path.collection,
                        ref: ref,
                    },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var ref = _a.ref;
            return ref;
        }), shareReplay(1));
        var subscriptionStream = key1 === 'onlineOffline'
            ? this.webSocketService
                .subscribeOnlineOffline(refStream.pipe(map(function (_a) {
                var _b = _a.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
                return ids;
            })))
                .pipe(combineLatest(refStream, function (ss, ref) { return ref; }))
            : refStream;
        return new ObjectObservable(Observable.merge(refStream, subscriptionStream).pipe(switchMap(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
            return _this.store.pipe(select(collection, authUid), filter(isDefined), map(function (obj) { return obj.entities[ids[0]] && obj.entities[ids[0]].entity; }), filter(isDefined), distinctUntilChanged());
        }), shareReplay(1)));
    };
    /* Returns a list of objects at a location.
     * If an observable is passed in for key2 then only the delta will be
     * refetched from the network.
     */
    Datastore.prototype.objects = function (key1, key2, objectIds) {
        var _this = this;
        var refStream = Observable.combineLatest([
            toObservable(key1),
            toObservable(key2),
            toObservable(objectIds).pipe(distinctUntilChanged(arrayIsShallowEqual)),
        ], function (collection, authUid, ids) {
            var path = {
                collection: collection,
                authUid: authUid,
                ids: ids.map(function (id) { return id.toString(); }),
            };
            return { path: path };
        }).pipe(
        // Add an path at the start with an empty list of ids
        duplicateFirst(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid;
            return ({
                path: { collection: collection, authUid: authUid, ids: [] },
            });
        }), pairwise(), // Use pairwise and only fetch new things
        switchMap(function (_a) {
            var _b = _a[0].path, collection = _b.collection, authUid = _b.authUid;
            return _this.store.pipe(select(collection, authUid), take(1));
        }, function (paths, data) { return ({ paths: paths, data: data }); }), tap(function (_a) {
            var _b = _a.paths, idsOrig = _b[0].path.ids, _c = _b[1].path, collection = _c.collection, authUid = _c.authUid, idsNew = _c.ids, data = _a.data;
            var newIds = setDiff(idsNew, idsOrig).filter(function (id) {
                return data && data.entities[id]
                    ? toNumber(data.entities[id].timeFetched) + 10000 < Date.now()
                    : true;
            });
            if (newIds.length) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: {
                        type: collection,
                        ref: { path: { collection: collection, authUid: authUid, ids: newIds } },
                    },
                };
                _this.store.dispatch(action);
            }
        }), map(function (_a) {
            var _b = _a.paths, collection = _b[0].path, authUid = _b[1].path;
            return ({
                path: authUid,
            });
        }), shareReplay(1));
        var subscriptionStream = key1 === 'onlineOffline'
            ? this.webSocketService
                .subscribeOnlineOffline(refStream.pipe(map(function (_a) {
                var _b = _a.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
                return ids;
            })))
                .pipe(combineLatest(refStream, function (ss, ref) { return ref; }))
            : refStream;
        return Observable.merge(refStream, subscriptionStream).pipe(switchMap(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
            return _this.store.pipe(tap(function (source) {
                if (!(collection in source)) {
                    throw new Error("Missing collection \"" + collection + "\", please import the corresponding feature module");
                }
            }), select(collection, authUid), filter(isDefined), map(function (entitiesObject) {
                return objectFilterByKeys(entitiesObject.entities, ids);
            }), map(function (entitiesObject) { return Object.values(entitiesObject); }), map(function (entities) { return entities.map(function (entity) { return entity.entity; }); }), distinctUntilChanged(sameElements));
        }), shareReplay(1));
    };
    // Hits an elastic search endpoint directly and returns the result.
    // The data doesn't get stored in the datastore (at least yet).
    Datastore.prototype.search = function (key, query) {
        var _this = this;
        return this.apiHttp
            .fetchSearch(key, toObservable(query))
            .pipe(map(function (result) { return _this.searchManager.transform(key, result); }));
    };
    /* Creates a single object.
    */
    Datastore.prototype.createObject = function (key1, key2, object, extra) {
        var _this = this;
        return Observable.combineLatest([toObservable(key1), toObservable(key2)], function (path1, path2) {
            var path = {
                collection: path1,
                authUid: path2,
            };
            return { path: path };
        })
            .pipe(take(1), switchMap(function (ref) {
            return push(ref, object, _this.store, _this.apiHttp, _this.errorTracking, extra);
        }))
            .toPromise();
    };
    return Datastore;
}());
export { Datastore };



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/datastore.ts.pre-build-optimizer.js